import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReveal } from '../../hooks/useReveal';
import SectionHead from './SectionHead';
import Icon from './Icon';

/**
 * Seção de demonstração em vídeo, usada pelas páginas de produto.
 *
 * NÃO dá autoplay, e isso é deliberado: o público destas páginas é
 * agrônomo em campo, com 4G irregular — o mesmo argumento que levou a
 * enxugar as imagens do site vale dobrado para megabytes de vídeo. Fica em
 * `preload="none"` atrás do pôster e só baixa quando alguém pede.
 *
 * `base` é o caminho sem extensão: o componente monta o <source> WebM e o
 * MP4 a partir dele. Os arquivos levam sufixo de versão porque `/assets/`
 * é servido com uma semana de cache atrás do CDN — trocar o vídeo mantendo
 * o nome não chega a quem já visitou.
 */
export default function DemoVideo({ i18nKey, base, poster, background = 'var(--p2a-dark-bg)' }) {
  const { t } = useTranslation();
  const ref = useReveal({ stagger: 0.07 });
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  function play() {
    const v = videoRef.current;
    if (!v) return;
    setPlaying(true);
    v.play().catch(() => setPlaying(false));   // bloqueado pelo browser: volta ao pôster
  }

  return (
    <section
      ref={ref}
      style={{ background, borderBottom: '1px solid var(--p2a-dark-border)', padding: '96px 0' }}
    >
      <div className="p2-container">
        <SectionHead
          eyebrow={t(`${i18nKey}.eyebrow`)}
          title={t(`${i18nKey}.title`)}
          lede={t(`${i18nKey}.lede`)}
        />
        {/* Largura cheia: o vídeo mostra a barra lateral do produto junto com
            o mapa, e numa coluna estreita os números ficam ilegíveis. */}
        <div
          className="reveal"
          style={{
            marginTop: 48, position: 'relative', borderRadius: 10, overflow: 'hidden',
            border: '1px solid var(--p2a-dark-border-2)', background: '#000', aspectRatio: '16 / 9',
          }}
        >
          <video
            ref={videoRef}
            poster={poster}
            preload="none"
            muted
            loop
            playsInline
            controls={playing}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          >
            {/* WebM primeiro (menor, escolhido por Chrome/Firefox); MP4
                atende Safari e iOS. */}
            <source src={`${base}.webm`} type="video/webm" />
            <source src={`${base}.mp4`} type="video/mp4" />
          </video>

          {!playing && (
            <button
              type="button"
              onClick={play}
              aria-label={t(`${i18nKey}.play`)}
              style={{
                position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 14,
                background: 'rgba(5,7,10,0.34)', border: 'none', cursor: 'pointer',
                color: 'var(--p2a-dark-fg-1)',
              }}
            >
              <span
                style={{
                  width: 74, height: 74, borderRadius: '50%', background: 'var(--p2a-green-500)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 8px 32px rgba(34,197,94,0.35)',
                }}
              >
                <Icon.ArrowRight width={26} height={26} />
              </span>
              <span className="mono" style={{ fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
                {t(`${i18nKey}.play`)}
              </span>
            </button>
          )}
        </div>
        <p className="reveal mono" style={{ marginTop: 16, fontSize: 12, color: 'var(--p2a-dark-fg-3)', letterSpacing: '0.04em' }}>
          {t(`${i18nKey}.caption`)}
        </p>
      </div>
    </section>
  );
}
